// src/components/BGP/request/request.ts
import axios from "axios";

import { BgpRoutingTable } from "../BgpData";

// Base de la API y /v1
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const API_V1_URL = `${API_BASE_URL}/v1`;

// Opcionales: Para los query params de CTXR
interface ParamsCTXRList {
    cloud?: string;
    region?: string;
}

/**
 * 1) GET /v1/ctxr/{subscriptionId}/listSelector?tenantId=...
 *    Devuelve la lista de CTX-Rs (id, name, description).
 */
export function getCTXRlistGlobal(
    subscriptionId: number,
    tenant: number,
    token: string,
    cloud?: string,
    region?: string
) {
    const params: ParamsCTXRList = {};
    if (cloud !== undefined) params.cloud = cloud;
    if (region !== undefined) params.region = region;

    return axios.get(
        `${API_V1_URL}/ctxr/${subscriptionId}/listSelector?tenantId=${tenant}`,
        {
            params,
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

/**
 * 2) GET /v1/ctxr/-1/statusneighbor?tenantId=1
 *    Devuelve array con { neighbor, as, up, state, ctxr, ... } (no se usa en BgpPage)
 */
export function getNeighborsStatus(token: string, tenantId: number) {
    return axios.get(
        `${API_V1_URL}/ctxr/-1/statusneighbor?tenantId=${tenantId}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

/**
 * 3) GET /v1/ctxr/listconfigbgpneighbor/{ctxrId}?tenantId=1
 *    Devuelve array con { id, ipid, router_id, local_address, local_as, peer_as, ... }
 */
export function getBgpNeighborsByCtxr(token: string, ctxrId: string, tenantId: number) {
    return axios.get(
        `${API_V1_URL}/ctxr/listconfigbgpneighbor/${ctxrId}?tenantId=${tenantId}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

/**
 * 4) GET /v1/ctxr/listcni/{ctxrId}?tenantId=1
 *    Devuelve [{ "name": "ens5", "value": "10.100.0.36" }, ... ]
 */
export function getLocalAddressesByCtxrId(token: string, ctxrId: string, tenantId: number) {
    return axios.get(`${API_V1_URL}/ctxr/listcni/${ctxrId}?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * 5) PUT /v1/ctxr/updateconfigbgpneighbor/{ctxrId}/{neighborId}?tenantId=1
 *    Actualiza un BGP neighbor
 */
export interface UpdateBgpNeighborPayload {
    ipid: string;
    router_id: string;
    local_address: string;
    local_as: number;
    peer_as: number;
    bgpfamily: string[]; // e.g. ["ipv4 unicast", "ipv4 mpls-vpn"]
    holdtime: number;    // e.g. 3
    description: string;
    addPath?: number; // Optional add-path capability: 0=disabled, 1=send, 2=receive, 3=send_receive
    // Required: the backend clears the neighbor's routing tables when the PUT omits them
    routingTables: BgpRoutingTable[] | null;
}

export function updateBgpNeighbor(
    token: string,
    ctxrId: string,
    neighborId: number,
    tenantId: number,
    payload: UpdateBgpNeighborPayload
) {
    return axios.put(
        `${API_V1_URL}/ctxr/updateconfigbgpneighbor/${ctxrId}/${neighborId}?tenantId=${tenantId}`,
        payload,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

/**
 * 6) POST /v1/ctxr/configbgpneighbor/{ctxrId}?tenantId=1
 *    Crea un BGP neighbor.
 */
export interface CreateBgpNeighborPayload {
    ipid: string;
    router_id: string;
    local_address: string;
    local_as: number;
    peer_as: number;
    typeConfig: number; // normal = 0
    bgpfamily: string[];
    holdtime: number;   // p.ej: 5
    bgppassword: string; // ""
    description: string;
    addPath?: number; // Optional add-path capability: 0=disabled, 1=send, 2=receive, 3=send_receive
    routingTables: BgpRoutingTable[] | null;
}

export function createBgpNeighbor(
    token: string,
    ctxrId: string,
    tenantId: number,
    payload: CreateBgpNeighborPayload
) {
    return axios.post(
        `${API_V1_URL}/ctxr/configbgpneighbor/${ctxrId}?tenantId=${tenantId}`,
        payload,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

/**
 * 7) DELETE /v1/ctxr/deleteconfigbgpneighbor/{ctxrId}/{neighborId}?tenantId=1
 *    Elimina un BGP neighbor.
 */
export function deleteBgpNeighbor(
    token: string,
    ctxrId: string,
    neighborId: number,
    tenantId: number
) {
    return axios.delete(
        `${API_V1_URL}/ctxr/deleteconfigbgpneighbor/${ctxrId}/${neighborId}?tenantId=${tenantId}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

/**
 * 8) GET /v1/tenant/partners
 *    Devuelve la lista de partners.
 */
export function getTenantPartners(token: string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_V1_URL}/tenant/partners?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * 9) GET /v1/tenant/customers
 *    Devuelve la lista de customers.
 */
export function getTenantCustomers(token: string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_V1_URL}/tenant/customers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
